/**
 * @file Composants stylés neutres du flow de réservation.
 * @module packages.common.components.table-reservation.styles.ReservationFlowStyles
 *
 * Styles agnostiques de l'hôte, sans dépendance à l'iframe : le flow passe
 * toujours `embedded` et `displayMode` explicitement, le positionnement ne se
 * déduit jamais d'une détection d'iframe.
 *
 * Le mode `displayMode === 'page'` correspond à l'hôte site public : header et
 * footer restent alors dans le flux naturel (`position: static`) pour ne pas
 * chevaucher le chrome de la page. En contexte widget (`embedded` à `true`), le
 * header et le footer sont fixes/sticky.
 */
import {styled} from '@mui/material/styles';
import {Box, Paper} from '@mui/material';
import {createGradientFromColor} from 'packages/common/utils/colorUtils';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';
import {typography} from 'packages/common/theme/typography';

// Conteneur principal du flow.
export const WidgetContainer = styled(Paper, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode', 'maxWidth', 'maxHeight', 'widgetType'].includes(prop),
})(({
  theme,
  embedded = false,
  displayMode = 'fixed',
  maxWidth = '700px',
  maxHeight = '650px',
  widgetType = 'reservations',
}) => ({
  width: '100%',
  maxWidth: embedded ? '100%' : maxWidth,
  margin: embedded ? '0' : '0 auto',
  height: embedded && displayMode === 'floating' ? 'auto' : embedded ? '100%' : 'auto',
  maxHeight: embedded ? 'none' : maxHeight,
  minHeight: embedded && displayMode === 'floating' ? '400px' : 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: embedded ? 0 : 12,
  boxShadow: embedded ?
    'none' :
    (widgetType === 'reservations' ? theme.shadows[10] : 'none'),
  backgroundColor: widgetType === 'giftvouchers' ? 'transparent' : theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  // Transition fluide pendant les changements de hauteur (hors iframe).
  transition: embedded ? 'none' : 'height 0.3s ease',

  // Optimisations mobile pour le mode flottant embarqué.
  ...(embedded && displayMode === 'floating' && {
    '@media (max-width: 480px)': {
      height: '100%',
      height: '-moz-available',
      height: '-webkit-fill-available',
      height: 'stretch',
      maxHeight: '100%',
      maxHeight: '-moz-available',
      maxHeight: '-webkit-fill-available',
      maxHeight: 'stretch',
      width: '100vw',
      borderRadius: 0,
      backgroundColor: theme.palette.background.paper,
    },
  }),
}));

// En-tête : dégradé dérivé de la couleur primaire, texte contrasté.
export const WidgetHeader = styled(Box, {
  shouldForwardProp: (prop) => !['variant', 'primaryColor'].includes(prop),
})(({theme, variant = 'default', primaryColor}) => {
  const contrastTextColor = primaryColor ?
    getContrastTextColor(primaryColor) :
    theme.palette.primary.contrastText;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    ...(variant === 'default' && {
      background: primaryColor && primaryColor.startsWith('#') ?
        createGradientFromColor(primaryColor, {
          darkenAmount: 20,
          angle: 45,
          startPosition: 30,
          endPosition: 90,
          useDarkerStart: true,
        }) :
        `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
      color: contrastTextColor,
    }),
    ...(variant === 'warning' && {
      background: `linear-gradient(45deg, ${theme.palette.warning.dark} 30%, ${theme.palette.warning.main} 90%)`,
      color: theme.palette.warning.contrastText,
    }),
    ...(variant === 'error' && {
      background: `linear-gradient(45deg, ${theme.palette.error.dark} 30%, ${theme.palette.error.main} 90%)`,
      color: theme.palette.error.contrastText,
    }),
  };
});

// Conteneur d'en-tête. En mode `page` (site public), reste dans le flux naturel
// pour ne pas chevaucher le chrome de la page ; sinon comportement widget
// historique (fixed embarqué, sticky autonome).
export const FixedHeaderContainer = styled(Box, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode'].includes(prop),
})(({theme, embedded = false, displayMode = 'fixed'}) => {
  const isPage = displayMode === 'page';
  return {
    position: isPage ? 'static' : (embedded ? 'fixed' : 'sticky'),
    ...(isPage ? {} : {top: 0, left: 0, right: 0, zIndex: 9999}),
    backgroundColor: 'background.paper',
    borderBottom: `1px solid ${theme.palette.divider}`,
    width: '100%',
    margin: embedded ? '0' : '0 auto',
  };
});

// Hauteurs de repli des éléments fixes, le temps que le DOM soit posé et mesuré.
// Les figer durablement se paie des deux côtés : une bande blanche quand la
// réserve dépasse le rendu réel, du contenu masqué quand elle lui est inférieure
// (titre sur deux lignes, langue plus verbeuse, pied sans branding).
const HEADER_FALLBACK_WITH_STEPS = 132;
const HEADER_FALLBACK = 64;
const FOOTER_FALLBACK = 120;

// Espaceur compensant la hauteur de l'en-tête fixe (embarqué uniquement).
export const HeaderSpacerBox = styled(Box, {
  shouldForwardProp: (prop) => !['hasSteps', 'embedded', 'measuredHeight'].includes(prop),
})(({hasSteps = true, embedded = false, measuredHeight = null}) => ({
  height: !embedded ?
    0 :
    `${measuredHeight ?? (hasSteps ? HEADER_FALLBACK_WITH_STEPS : HEADER_FALLBACK)}px`,
  flexShrink: 0,
  display: embedded ? 'block' : 'none',
}));

// Zone de contenu défilable.
export const ScrollableContentBox = styled(Box, {
  shouldForwardProp: (prop) => !['hasFooter', 'embedded', 'displayMode', 'footerHeight'].includes(prop),
})(({theme, hasFooter = true, embedded = false, displayMode = 'fixed', footerHeight = null}) => {
  // En flottant embarqué, l'iframe occupe 100% de l'écran : le scroll se fait
  // dans ce conteneur. En fixe embarqué sans footer, l'iframe se redimensionne
  // au contenu via les messages : overflow visible pour laisser mesurer.
  const isFloatingEmbedded = embedded && displayMode === 'floating';

  return {
    flexGrow: 1,
    minHeight: 0,
    overflowY: (embedded && !hasFooter && !isFloatingEmbedded) ? 'visible' : 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(2),
    // Réserve la place du pied fixe (StepActions + branding), mesurée au rendu.
    paddingBottom: (hasFooter && embedded) ? `${footerHeight ?? FOOTER_FALLBACK}px` : 0,
  };
});

// Conteneur de pied de page. Comme l'en-tête : flux naturel en mode `page`,
// fixed/sticky sinon.
export const FixedFooterContainer = styled(Box, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode'].includes(prop),
})(({theme, embedded = false, displayMode = 'fixed'}) => {
  const isPage = displayMode === 'page';
  return {
    position: isPage ? 'static' : (embedded ? 'fixed' : 'sticky'),
    ...(isPage ? {} : {bottom: 0, left: 0, right: 0, zIndex: 9999}),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    width: embedded ? '100%' : 'inherit',
    maxWidth: embedded ? '100%' : '600px',
    margin: embedded ? '0' : '0 auto',
  };
});

/**
 * Style commun des champs de saisie du parcours (contact, téléphone, allergies,
 * occasion, demandes spéciales, bon cadeau), à fusionner dans un `TextField` ou un
 * `OutlinedInput`. Survol et focus restent ceux du thème, comme au dashboard.
 *
 * Texte saisi à la taille du texte courant à la souris, mais 16 px sur écran
 * tactile : c'est le minimum en dessous duquel Safari iOS zoome sur le champ
 * touché, lecture seule comprise. Le dashboard et le site public bloquent ce
 * zoom par leur balise viewport, mais le widget embarqué dépend de celle du
 * site hôte : seule la taille de police le protège. Le texte d'exemple reste à
 * la taille courante dans les deux cas, sans effet sur le zoom.
 */
export const clientInputStyles = {
  '& .MuiInputBase-input': {
    'fontSize': typography.body2.fontSize,
    '&::placeholder': {
      fontSize: typography.body2.fontSize,
    },
    '@media (pointer: coarse)': {
      fontSize: '16px',
    },
  },
};
